import React, { useLayoutEffect, useState } from "react";
import BpCheckbox from "view/components/CheckBox";
import apiLibrary from "services/api";
import InputField from "view/components/InputField";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import ellipsize from "ellipsize";
import { filtersInitialState } from "store/filters/initialState";
import _ from "lodash";
import InfiniteScroll from "react-infinite-scroll-component";
import { TailSpin } from "react-loader-spinner";
import useCustomBranding from "hooks/useCustomBranding";
import { programsBrandsAction } from "store/filters/reducer.actions";

export const Brands: React.FC = () => {
  const { programsFilter } = useSelector<RootState, filtersInitialState>(
    (state) => state.Filters
  );
  const dispatch = useDispatch();
  const { primaryColor } = useCustomBranding();

  const [dropDownValues, setDropDownValues] = useState<any[]>([]);
  const [searchString, setSearchString] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [page, setPage] = useState(1);

  const handleSelection = (
    _e: React.ChangeEvent<HTMLInputElement>,
    selectedValue: any
  ) => {
    dispatch(programsBrandsAction(selectedValue));
  };

  const fetchBrandDropdownData = async (
    isLoadMore: boolean,
    pageNumber: number = 1,
    search?: string
  ) => {
    setIsLoading(true);
    try {
      const response = await apiLibrary.userProfile.getUsersFilterDropdowns(
        pageNumber,
        "brands",
        search
      );
      const mappedBrands =
        response.data.brands?.map((brand: any) => ({
          id: brand.app_id ?? brand.appId ?? brand.id,
          name: brand.name,
        })) ?? [];
      if (isLoadMore) {
        setDropDownValues((pre: any[]) => {
          return [...pre, ...mappedBrands];
        });
      } else {
        setDropDownValues(mappedBrands);
      }
      setHasMoreData(response.data.totalPages > pageNumber);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Failed to fetch brand dropdown values:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useLayoutEffect(() => {
    fetchBrandDropdownData(false, 1);
  }, []);

  const handleSearchEntities = (value: string): void => {
    setPage((pre: number) => {
      fetchBrandDropdownData(false, 1, value);
      return 1;
    });
  };

  const debouncedSearch = _.debounce(handleSearchEntities, 100);

  const handleLoadMore = () => {
    setPage((prevPage) => {
      const nextPage = prevPage + 1;
      fetchBrandDropdownData(true, nextPage, searchString);
      return nextPage;
    });
  };

  return (
    <div className="bg-bgWhite dark:bg-secondaryLight min-w-[315px] shadow-xl rounded-xl flex flex-col justify-start items-start relative border border-lineMid dark:border-lineLight">
      <div className="w-full">
        <div className="w-full pl-3 pr-3 mt-2 mb-3 bg-bgWhite dark:bg-secondaryLight">
          <InputField
            placeholder="Search"
            type="Search"
            onChange={(e: any) => {
              setSearchString(e.target.value);
              debouncedSearch(e.target.value);
            }}
          />
        </div>
        <div className="pl-1.5 bg-bgWhite dark:bg-secondaryLight">
          <InfiniteScroll
            dataLength={dropDownValues.length}
            next={handleLoadMore}
            hasMore={hasMoreData}
            height={400}
            loader={
              <div className="flex items-center justify-center w-full loader">
                <TailSpin
                  height="30"
                  width="30"
                  color={primaryColor}
                  ariaLabel="tail-spin-loading"
                  radius="2"
                  wrapperStyle={{}}
                  wrapperClass="tailspin-loader"
                  visible={true}
                />
              </div>
            }
            pullDownToRefresh={true}
            refreshFunction={() => {}}
            pullDownToRefreshContent={
              <h3 style={{ textAlign: "center" }}>
                &#8595; Pull down to refresh &#8595;
              </h3>
            }
            releaseToRefreshContent={
              <h3 style={{ textAlign: "center" }}>
                &#8593; Release to refresh &#8593;
              </h3>
            }
          >
            {dropDownValues.length === 0 && !isLoading && (
              <div className="w-full flex items-center justify-center py-32 text-textMid font-semibold dark:text-inputText">
                No Record Found
              </div>
            )}

            {hasMoreData && isLoading && (
              <div className="flex items-center justify-center w-full loader">
                <TailSpin
                  height="30"
                  width="30"
                  color={primaryColor}
                  ariaLabel="tail-spin-loading"
                  radius="2"
                  wrapperStyle={{}}
                  wrapperClass="tailspin-loader"
                  visible={true}
                />
              </div>
            )}
            {dropDownValues.map((dropDown: any, index: any) => (
              <div
                key={index}
                className="flex items-center justify-start w-full px-1 rounded-lg"
              >
                <BpCheckbox
                  checked={programsFilter.brandIds.value.some(
                    (item: any) => item.id === dropDown.id
                  )}
                  label={
                    <div className="flex whitespace-nowrap justify-start items-center flex-grow relative py-0.5">
                      <p className="dark:text-inputText">
                        {ellipsize(`${dropDown.name}`, 25)}
                      </p>
                    </div>
                  }
                  labelPlacement="end"
                  onChange={(e) => handleSelection(e, dropDown)}
                />
              </div>
            ))}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  );
};
